import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Grid,
  Stack,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiService } from '../services/api.js';

const BookingsPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const loadingRef = useRef(false);


  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    loadBookings();
  }, [isAuthenticated]);

  const loadBookings = async () => {
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await apiService.getBookings();
      
      // API returns { bookings: [...], total_count: number }
      if (response.data?.bookings && Array.isArray(response.data.bookings)) {
        setBookings(response.data.bookings);
      } else if (response.data && typeof response.data === 'object') {
        // Check if it's an empty response with bookings: []
        setBookings([]);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setError('Failed to load bookings. Please try again.');
      setBookings([]); // Ensure bookings is always an array
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };


  const handleCancelBooking = async () => {
    if (!selectedBooking) {
      return;
    }

    setCancelling(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await apiService.cancelBooking(selectedBooking.booking_id);
      
      // Update local state to reflect cancellation
      setBookings(prev => 
        prev.map(booking => 
          booking.booking_id === selectedBooking.booking_id 
            ? { ...booking, status: 'cancelled', payment_status: 'refund_pending' }
            : booking
        )
      );

      setCancelDialogOpen(false);
      setSelectedBooking(null); // Clear selected booking after successful cancellation
      
      // Show success feedback
      setSuccessMessage('Booking cancelled successfully. Refund will be processed within 3-5 business days.');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      
      // Show error feedback
      if (error.response?.status === 400) {
        setError('This booking cannot be cancelled. It may be too close to the appointment time or already completed.');
      } else if (error.response?.status === 404) {
        setError('Booking not found. It may have already been cancelled.');
      } else {
        setError('Failed to cancel booking. Please try again or contact support.');
      }
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    
    // If time is already in AM/PM format, return as is
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    
    // Otherwise, convert from 24-hour format
    try {
      return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting time:', error, time);
      return time;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircleIcon color="success" />;
      case 'completed':
        return <CheckCircleIcon color="primary" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'completed':
        return 'primary';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const filterBookings = (bookings, filterType) => {
    // Safety check: ensure bookings is an array
    if (!Array.isArray(bookings)) {
      return [];
    }
    
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of today for accurate date comparison
    let filtered = [];
    
    switch (filterType) {
      case 0: // Upcoming
        filtered = bookings.filter(booking => {
          const bookingDate = new Date(booking.booking_date);
          bookingDate.setHours(0, 0, 0, 0);
          const isUpcoming = bookingDate >= now && booking.status !== 'cancelled' && booking.status !== 'completed';
          return isUpcoming;
        });
        break;
      case 1: // Past
        filtered = bookings.filter(booking => {
          const bookingDate = new Date(booking.booking_date);
          bookingDate.setHours(0, 0, 0, 0);
          const isPast = bookingDate < now && booking.status !== 'cancelled';
          return isPast;
        });
        break;
      case 2: // Cancelled
        filtered = bookings.filter(booking => {
          const isCancelled = booking.status === 'cancelled';
          return isCancelled;
        });
        break;
      default:
        filtered = bookings;
    }
    
    return filtered;
  };

  const canCancelBooking = (booking) => {
    // Only allow cancellation for pending or confirmed bookings
    if (booking.status !== 'pending' && booking.status !== 'confirmed') {
      return false;
    }

    // Check if booking is more than 24 hours away
    try {
      const now = new Date();
      const bookingDateTime = new Date(`${booking.booking_date}T${booking.booking_time}`);
      
      // Calculate the difference in hours
      const timeDifferenceMs = bookingDateTime.getTime() - now.getTime();
      const timeDifferenceHours = timeDifferenceMs / (1000 * 60 * 60);
      
      // Allow cancellation only if booking is more than 24 hours away
      return timeDifferenceHours > 24;
    } catch (error) {
      console.error('Error calculating booking time difference:', error);
      // If there's an error calculating time, don't allow cancellation for safety
      return false;
    }
  };

  const filteredBookings = filterBookings(bookings, tabValue);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Please sign in to view your bookings
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.8
              }
            }}
          >
            Go to Home
          </Button>
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography>{t('common.loading')}</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('bookings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your MOT test bookings
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`${t('bookings.upcoming')} (${filterBookings(bookings, 0).length})`} />
          <Tab label={`${t('bookings.past')} (${filterBookings(bookings, 1).length})`} />
          <Tab label={`${t('bookings.cancelled')} (${filterBookings(bookings, 2).length})`} />
        </Tabs>
      </Box>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {tabValue === 0 ? 'No upcoming bookings' : 
             tabValue === 1 ? 'No past bookings' : 'No cancelled bookings'}
          </Typography>
          {tabValue === 0 && (
            <Button 
              variant="contained" 
              onClick={() => navigate('/')}
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  opacity: 0.8
                }
              }}
            >
              Book MOT Test
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredBookings.map((booking) => (
            <Grid size={{ xs: 12, md: 6 }} key={booking.booking_id}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  {/* Status and Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Chip 
                      label={booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || 'Unknown'}
                      color={getStatusColor(booking.status)}
                      icon={getStatusIcon(booking.status)}
                      size="small"
                    />
                    
                    {/* Only show menu when cancellation is possible */}
                    {tabValue === 0 && canCancelBooking(booking) && (
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuOpen(e, booking)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Booking Reference */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Ref: {booking.booking_ref}
                  </Typography>

                  {/* Vehicle */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <CarIcon color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Vehicle
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 3.5 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {booking.vehicle_regno || 'Vehicle'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Garage */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <LocationIcon color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Garage
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 3.5 }}>
                      <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {booking.garage_name || 'Garage'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {booking.garage_address}
                      </Typography>
                      {booking.garage_postcode && (
                        <Typography variant="body2" color="text.secondary">
                          {booking.garage_postcode}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Appointment */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <ScheduleIcon color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Appointment
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 3.5 }}>
                      <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {formatDate(booking.booking_date)}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {formatTime(booking.booking_time)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Services */}
                  {(booking.products?.length > 0 || booking.addons?.length > 0) && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                          Services
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 3.5 }}>
                        <Stack spacing={0.5}>
                          {/* Products */}
                          {booking.products?.map((productName, index) => (
                            <Typography key={`product-${index}`} variant="body2" sx={{ fontWeight: 'medium' }}>
                              • {productName}
                            </Typography>
                          ))}
                          {/* Addons */}
                          {booking.addons?.map((addonName, index) => (
                            <Typography key={`addon-${index}`} variant="body2" color="text.secondary">
                              + {addonName}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                  )}

                  {/* Payment */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <ReceiptIcon color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Payment
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 3.5 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                        £{booking.total_amount ? booking.total_amount.toFixed(2) : '0.00'}
                      </Typography>
                      <Chip 
                        label={
                          booking.status === 'cancelled' && booking.refund_status ? 
                            `Refund: ${booking.refund_status.charAt(0).toUpperCase() + booking.refund_status.slice(1)}` :
                            booking.payment_status === 'paid' ? 'Paid' : 'Pending Payment'
                        }
                        color={
                          booking.status === 'cancelled' && booking.refund_status === 'completed' ? 'success' :
                          booking.status === 'cancelled' && booking.refund_status === 'pending' ? 'warning' :
                          booking.status === 'cancelled' && booking.refund_status === 'failed' ? 'error' :
                          booking.payment_status === 'paid' ? 'success' : 'warning'
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                </CardContent>

                {/* Actions */}
                {(booking.status === 'confirmed' || booking.status === 'pending') && (
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {canCancelBooking(booking) 
                        ? 'You can cancel this booking up to 24 hours before your appointment'
                        : booking.status === 'cancelled' 
                          ? 'This booking has been cancelled'
                          : 'Cancellation not allowed within 24 hours of appointment'
                      }
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disablePortal={false}
        keepMounted={false}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedBooking && canCancelBooking(selectedBooking) && (
          <MenuItem onClick={() => {
            setCancelDialogOpen(true);
            setAnchorEl(null); // Close menu but keep selectedBooking
          }}>
            <CancelIcon sx={{ mr: 1 }} fontSize="small" />
            Cancel
          </MenuItem>
        )}
      </Menu>

      {/* Cancel Confirmation Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => {
          setCancelDialogOpen(false);
          // Don't clear selectedBooking here - let the button handlers manage it
        }}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        disablePortal={false}
        keepMounted={false}
      >
        <DialogTitle id="cancel-dialog-title">Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography id="cancel-dialog-description" component="div">
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
          {selectedBooking && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>Vehicle:</strong> {selectedBooking.vehicle_regno || 'Vehicle'}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>Date:</strong> {formatDate(selectedBooking.booking_date)} at {formatTime(selectedBooking.booking_time)}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>Amount:</strong> £{selectedBooking.total_amount ? selectedBooking.total_amount.toFixed(2) : '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>Refund:</strong> Processing refunds can take 3-5 business days
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelDialogOpen(false);
              setSelectedBooking(null);
            }}
            autoFocus
          >
            Keep Booking
          </Button>
          <Button 
            onClick={() => {
              handleCancelBooking();
            }} 
            color="error" 
            variant="contained"
            disabled={cancelling}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'error.main',
                color: 'white',
                opacity: 0.8
              }
            }}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingsPage;